/**
 * 공지사항 팝업 모달
 * 로그인 시 읽지 않은 팝업 공지를 표시
 * 각 공지마다 별도의 팝업으로 표시됨
 * 드래그로 이동 가능, 클릭 시 맨 앞으로 이동
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Notice } from '../../types/notice.types';
import { format } from 'date-fns';
import '../../styles/NoticePopupModal.css';

interface NoticePopupModalProps {
    notice: Notice;
    index: number;
    zIndex: number;
    onClose: () => void;
    onHideForToday: (noticeId: number) => void;
    onBringToFront: (noticeId: number) => void;
}

export const NoticePopupModal: React.FC<NoticePopupModalProps> = ({
    notice,
    index,
    zIndex,
    onClose,
    onHideForToday,
    onBringToFront
}) => {
    const [hideForToday, setHideForToday] = useState(false);

    // 화면 중앙 계산 함수
    const calculateCenterPosition = useCallback(() => {
        const modalWidth = 480; // max-width
        const modalHeight = 600; // 예상 높이
        const x = (window.innerWidth - modalWidth) / 2 + index * 30;
        const y = (window.innerHeight - modalHeight) / 2 + index * 30;
        return { x: Math.max(0, x), y: Math.max(0, y) };
    }, [index]);

    // 드래그 관련 상태 - 상단 좌측 기준 위치
    const [position, setPosition] = useState(calculateCenterPosition());
    const [isDragging, setIsDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const modalRef = useRef<HTMLDivElement>(null);

    const handleConfirm = () => {
        if (hideForToday) {
            onHideForToday(notice.noticeId);
        }
        onClose();
    };

    // 모달 클릭 시 맨 앞으로
    const handleModalClick = () => {
        onBringToFront(notice.noticeId);
    };

    // 드래그 시작
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault(); // 텍스트 선택 방지
        onBringToFront(notice.noticeId);
        if (modalRef.current) {
            dragOffset.current = {
                x: e.clientX - position.x,
                y: e.clientY - position.y
            };
            setIsDragging(true);
            // 드래그 중 전체 페이지 텍스트 선택 방지
            document.body.style.userSelect = 'none';
        }
    }, [notice.noticeId, onBringToFront, position.x, position.y]);

    // 드래그 중
    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            e.preventDefault();
            const newX = e.clientX - dragOffset.current.x;
            const newY = e.clientY - dragOffset.current.y;
            setPosition({ x: newX, y: newY });
        }
    }, [isDragging]);

    // 드래그 종료
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        // 텍스트 선택 방지 해제
        document.body.style.userSelect = '';
    }, []);

    // 전역 마우스 이벤트 등록
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // 화면 크기 변경 시 위치 재계산
    useEffect(() => {
        const handleResize = () => {
            if (!isDragging) {
                setPosition(calculateCenterPosition());
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isDragging, calculateCenterPosition]);

    const isUrgent = notice.urgentYn === 'Y';

    return (
        <div className="notice-popup-overlay" style={{ zIndex }}>
            <div
                ref={modalRef}
                className={`notice-popup-modal ${isDragging ? 'notice-popup-dragging' : ''} ${isUrgent ? 'notice-popup-urgent' : ''}`}
                style={{ left: position.x, top: position.y }}
                onMouseDown={handleModalClick}
            >
                <div
                    className="notice-popup-header notice-popup-draggable"
                    onMouseDown={handleMouseDown}
                >
                    <span className={`notice-popup-badge ${isUrgent ? 'notice-popup-badge-urgent' : ''}`}>
                        {isUrgent ? '긴급 공지' : '공지'}
                    </span>
                    <button
                        className="notice-popup-close"
                        onClick={onClose}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        &times;
                    </button>
                </div>

                <div className="notice-popup-body">
                    <h3 className="notice-popup-title">{notice.title}</h3>
                    <div className="notice-popup-meta">
                        <span className="notice-popup-date">
                            {notice.startDate
                                ? format(notice.startDate, 'yyyy-MM-dd HH:mm')
                                : '-'}
                        </span>
                        <span className="notice-popup-writer">
                            {notice.writerTypeName}
                        </span>
                    </div>
                    <div className="notice-popup-content">
                        {notice.content.split('\n').map((line, idx) => (
                            <p key={idx}>{line || '\u00A0'}</p>
                        ))}
                    </div>
                </div>

                <div className="notice-popup-footer">
                    <label className="notice-popup-checkbox-wrapper">
                        <input
                            type="checkbox"
                            className="notice-popup-checkbox"
                            checked={hideForToday}
                            onChange={(e) => setHideForToday(e.target.checked)}
                        />
                        <span className="notice-popup-checkbox-label">오늘 하루 보지 않기</span>
                    </label>
                    <div className="notice-popup-buttons">
                        <button
                            className="notice-popup-btn-confirm"
                            onClick={handleConfirm}
                        >
                            확인
                        </button>
                    </div>
                </div>

                <div className="notice-popup-logo-area">
                    <img
                        src="/images/vroong-logo.svg"
                        alt="부릉라이더스"
                        className="notice-popup-logo"
                    />
                </div>
            </div>
        </div>
    );
};

export default NoticePopupModal;
